import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { ref, query, orderByChild, equalTo, startAt, get } from 'firebase/database';
import { useConfig } from '../context/ConfigContext';
import '../css/BookingStatusScreen.css';
import {
  BOOKING_STATUS_TITLE,
  BOOKING_STATUS_SUBTITLE,
  BOOKING_STATUS_CONFIRMED_MESSAGE,
  DEFAULT_COURT_NAME,
} from '../constants/bookingConst';
import { groupBookingRecords } from '../utils/bookingGroupUtil';
import { spinnerRing, getCourtLabel, formatTimeSlotRange, extractGuestCode } from '../utils/bookingUtils';
import { formatBookingDate } from '../utils/bookingStatusFormatUtil';
import { findOpenPlayJoinByConfirmationId } from '../utils/openPlayUtil';
import ConnectionErrorScreen from './ConnectionErrorScreen';
import BookingStatusScheduleBoxes from './BookingStatusScheduleBoxes';
import BookingStatusContactBox from './BookingStatusContactBox';
import BookingStatusPaymentBox from './BookingStatusPaymentBox';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';

/**
 * Reshapes an openPlayJoins record into what this screen renders, so an Open
 * Play reference resolves through the same path a booking does instead of
 * needing a parallel screen.
 *
 * Open Play joins live in their own Firebase node and were invisible here —
 * searching a valid Open Play reference answered "No booking found", which
 * to a guest who has already paid reads as their money having vanished.
 *
 * `isOpenPlayJoin` marks the parts that genuinely differ: a join has players
 * and a per-player fee, not courts and hours, so the empty `timeSlots` keeps
 * the shared date/summary helpers working without inventing a court booking
 * that doesn't exist.
 *
 * @param {object} join - an openPlayJoins record including its Firebase `id`
 * @returns {object}
 */
const openPlayJoinToStatusRecord = (join) => ({
  ...join,
  isOpenPlayJoin: true,
  userName: join.fullName,
  paymentAmount: join.totalAmount,
  timeSlots: {},
  dates: [{ date: join.date, timeSlots: {} }],
});

// How far back a NAME search reaches. Confirmation-ID search is unlimited —
// holding the ID is proof enough. A name isn't, so name search is scoped to
// what a guest plausibly needs to check (an upcoming booking, or one from
// the last few weeks) rather than exposing a regular customer's whole
// history to anyone who knows what they're called.
const NAME_SEARCH_LOOKBACK_DAYS = 30;

/**
 * Confirmation IDs are <PREFIX>-<MMDDYYYY>-<random>, e.g.
 * "DPTC-08132026-XS9WLRQ5" — see generateConfirmationId in bookingUtils.js.
 * A name never takes that shape (no spaces, and a long digit run in the
 * middle), so the shape alone decides which lookup to run: one Firebase read
 * either way, instead of querying by ID then falling back to a second query.
 *
 * The digit run is \d{6,} rather than a fixed 8, so this covers the current
 * MMDDYYYY ids, an older epoch-millisecond format still sitting in some
 * records ("PB-1780377022355-OFCQ09MPO"), and any prefix/length a future
 * client is configured with.
 *
 * @param {string} term
 * @returns {boolean}
 */
const looksLikeConfirmationId = (term) => /^[A-Za-z][A-Za-z0-9]*-\d{6,}-[A-Za-z0-9]{3,}$/.test((term || '').trim());

/** Local 'yyyy-MM-dd' for a Date, matching how booking dates are stored. */
const dateKeyOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Longest the match-list summary line is allowed to get before it's cut.
// A backstop only — summarizeBooking below keeps it well under this for any
// realistic booking; this catches a club with a dozen oddly-named courts.
const MATCH_SUMMARY_MAX_CHARS = 90;

/** A grouped booking's earliest date, for sorting the match list. */
const firstDateOf = (booking) =>
  (booking?.dates || []).map(d => d.date).filter(Boolean).sort()[0] || booking?.date || '';

/**
 * @param {boolean} embedded - When true, renders for use inside
 *   TrackBookingModal instead of the standalone /bookings[:bookingId]
 *   route: searching doesn't push a new URL, and the "Book a Court" /
 *   "Book Another Court" buttons close the modal (via onClose) instead of
 *   navigating away, since the guest is likely already mid-flow behind it.
 * @param {string|null} initialBookingId - pre-fills and auto-searches, same
 *   as the route param does in standalone mode. Left null for the modal so
 *   it opens on an empty search form.
 * @param {() => void} onClose - only used when embedded.
 */
function BookingStatusScreen({ embedded = false, initialBookingId = null, onClose } = {}) {
  const params = useParams();
  const bookingId = embedded ? initialBookingId : params.bookingId;
  const navigate = useNavigate();
  const { config } = useConfig();
  const courtName = config?.courtName || DEFAULT_COURT_NAME;

  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Watchdog over the lookup below — a Firebase get() on a dead connection
  // never rejects, it just never resolves, so the spinner needs its own
  // deadline (see hooks/useLoadingTimeout.js).
  const { timedOut: searchTimedOut, restart: restartSearchTimeout } = useLoadingTimeout(loading);
  // What Try Again re-runs. Stored as a thunk rather than a {type, term} pair
  // so the retry can't drift from whichever lookup actually stalled — an ID
  // search and a name search take different Firebase paths.
  const lastSearchRef = useRef(null);
  const [searchId, setSearchId] = useState(bookingId || '');
  // Several reservations share the searched name — the guest chooses which
  // one to open rather than having one picked for them (see handlePickMatch).
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    if (bookingId) {
      setSearchId(bookingId);
      fetchBooking(bookingId);
    } else {
      setLoading(false);
    }
  }, [bookingId]);

  /**
   * Reads every record a query returned and stitches multi-date siblings
   * back together. A multi-date booking is stored as one Firebase record PER
   * DATE sharing a confirmationId/groupId (see App.jsx's
   * createPendingBooking), so both query paths below need this.
   */
  /**
   * Finds bookings by the reference code a club types after a guest's name —
   * "Bryan Namoc - RIP-20260814-N9UJB" is found by "RIP-20260814-N9UJB", and
   * NOT by "Bryan Namoc".
   *
   * The match has to happen in the browser. Realtime Database can query a
   * whole value or a prefix, and the code sits at the END of `userName`, so
   * no server-side query can reach it. What keeps that affordable is the
   * range query below: only bookings from the lookback window onward are
   * read, using the existing `date` index, rather than the whole table.
   *
   * Called only after a confirmation-ID lookup has already missed, so a guest
   * searching an ordinary ID never pays for this.
   *
   * @param {string} code
   * @returns {Promise<Array<object>>} grouped bookings carrying that code
   */
  const findByGuestCode = async (code) => {
    const wanted = code.trim().toLowerCase();
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - NAME_SEARCH_LOOKBACK_DAYS);

    const snapshot = await get(query(ref(db, 'bookings'), orderByChild('date'), startAt(dateKeyOf(cutoff))));
    if (!snapshot.exists()) return [];

    const matching = [];
    snapshot.forEach((child) => {
      const data = child.val() || {};
      // extractGuestCode is what defines "the code part", so an ordinary
      // name — including a hyphenated one — yields null and never matches.
      const found = extractGuestCode(data.userName);
      // Compared case-insensitively: unlike a Firebase equalTo, matching here
      // is ours to define, and a guest retyping a code shouldn't have to get
      // the capitals right.
      if (found && found.toLowerCase() === wanted) {
        matching.push({ id: child.key, ...data });
      }
    });

    return groupBookingRecords(matching)
      .filter(b => b.status !== 'cancelled')
      .sort((a, b) => (firstDateOf(a) || '').localeCompare(firstDateOf(b) || ''));
  };

  const groupSnapshot = (snapshot) => {
    const records = [];
    snapshot.forEach((child) => { records.push({ id: child.key, ...child.val() }); });
    return groupBookingRecords(records);
  };

  const fetchBooking = async (confirmationId) => {
    lastSearchRef.current = () => fetchBooking(confirmationId);
    if (!confirmationId || !db) {
      setError('Invalid booking ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setMatches([]);

    try {
      const bookingsRef = ref(db, 'bookings');
      const snapshot = await get(query(bookingsRef, orderByChild('confirmationId'), equalTo(confirmationId)));

      if (snapshot.exists()) {
        const [combined] = groupSnapshot(snapshot);
        setBookingData(combined);
        return;
      }

      // Not a court booking. Open Play joins are stored in their own node
      // with their own OP- reference, and are checked before the guest-code
      // fallback because an exact reference match is a stronger signal than
      // a code parsed out of somebody's name.
      //
      // Caught locally rather than left to the outer handler: this lookup is
      // one rung of a fallback chain for COURT bookings too, and a failure
      // reading openPlayJoins (denied by rules on a client where Open Play
      // isn't set up, a dropped connection) must not abort the guest-code
      // search below. Without this, an Open Play problem would surface as
      // "unable to retrieve booking" to someone searching an ordinary
      // reservation — a regression in a path that has nothing to do with it.
      let openPlayJoin = null;
      try {
        openPlayJoin = await findOpenPlayJoinByConfirmationId(confirmationId);
      } catch (openPlayError) {
        console.error('Open Play lookup failed; continuing with booking search:', openPlayError);
      }
      if (openPlayJoin) {
        setBookingData(openPlayJoinToStatusRecord(openPlayJoin));
        return;
      }

      // It may be a reference code a club typed after the guest's name —
      // "Bryan Namoc - RIP-20260814-N9UJB" — which is a separate thing that
      // happens to share the same shape.
      const byCode = await findByGuestCode(confirmationId);
      if (byCode.length === 1) {
        setBookingData(byCode[0]);
      } else if (byCode.length > 1) {
        setMatches(byCode);
      } else {
        setError('No booking or Open Play join found for that Confirmation ID or reference code. Please check it and try again.');
      }
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Unable to retrieve booking. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Looks a guest up by their exact booking name, for the common case of
   * having lost the confirmation ID.
   *
   * Deliberately EXACT rather than a prefix or contains match: a name is
   * guessable in a way a confirmation ID isn't, and every result exposes a
   * phone number, an email and a payment receipt. Exact-only means someone
   * has to already know the full name as it was typed at booking.
   *
   * NOTE: Realtime Database equalTo is case- and space-sensitive and there
   * is no way to make it otherwise, so "hissam" will not find "Hissam".
   * Making it forgiving would mean writing a normalized copy of the name
   * (e.g. userNameLower) on every booking and indexing that instead — a
   * schema change that also wouldn't cover existing records. Left exact for
   * now; the not-found message points at the Confirmation ID as the reliable
   * route.
   *
   * Scoped to reservations from NAME_SEARCH_LOOKBACK_DAYS ago onward for the
   * same reason — checking an upcoming booking is what this is for, and a
   * long-standing customer's entire history shouldn't be retrievable from
   * their name alone.
   *
   * @param {string} name
   */
  const fetchBookingsByName = async (name) => {
    lastSearchRef.current = () => fetchBookingsByName(name);
    setLoading(true);
    setError(null);
    setBookingData(null);
    setMatches([]);

    try {
      const bookingsRef = ref(db, 'bookings');
      // Needs `.indexOn: ["userName"]` on /bookings — without it Firebase
      // downloads the whole node and filters client-side, which on this
      // schema means every payment receipt image too.
      const q = query(bookingsRef, orderByChild('userName'), equalTo(name));
      const snapshot = await get(q);

      if (!snapshot.exists()) {
        setError(`No booking found under "${name}". The name has to match exactly as you typed it when booking (capitals included) — or search with your Confirmation ID instead.`);
        return;
      }

      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - NAME_SEARCH_LOOKBACK_DAYS);
      const cutoffStr = dateKeyOf(cutoff);

      const recent = groupSnapshot(snapshot)
        // A group is in range if ANY of its dates is — a multi-date booking
        // straddling the cutoff should still be findable.
        .filter(b => (b.dates || []).some(d => d.date && d.date >= cutoffStr))
        .filter(b => b.status !== 'cancelled')
        .sort((a, b) => (firstDateOf(a) || '').localeCompare(firstDateOf(b) || ''));

      if (recent.length === 0) {
        setError(`No recent booking found under "${name}". Older reservations can still be opened with their Confirmation ID.`);
      } else if (recent.length === 1) {
        setBookingData(recent[0]);
      } else {
        // Several — let the guest pick rather than guessing which they meant.
        setMatches(recent);
      }
    } catch (err) {
      console.error('Error searching bookings by name:', err);
      setError('Unable to search bookings. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Try Again on the timeout screen — restarts the countdown and re-runs
   * whichever lookup stalled.
   *
   * @returns {void}
   */
  const handleSearchRetry = () => {
    restartSearchTimeout();
    if (lastSearchRef.current) lastSearchRef.current();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const term = searchId.trim();
    if (!term) return;

    // A confirmation ID and a reference code are the same shape
    // (<letters>-<digits>-<alphanumerics>), so a code-shaped term can't be
    // told apart up front — fetchBooking tries confirmationId first, then
    // falls back to matching the code out of userName. Anything else is
    // treated as a name.
    if (looksLikeConfirmationId(term)) {
      // Embedded (modal) mode stays put — searching shouldn't navigate the
      // guest away from whatever screen opened it.
      if (!embedded) navigate(`/bookings/${term}`);
      fetchBooking(term);
    } else {
      fetchBookingsByName(term);
    }
  };

  /** Guest picked one of several name matches. */
  const handlePickMatch = (booking) => {
    setMatches([]);
    setBookingData(booking);
    if (!embedded && booking?.confirmationId) navigate(`/bookings/${booking.confirmationId}`);
  };

  /**
   * One short line describing a whole reservation, for the match list.
   *
   * The full slot listing is unusable there: a guest who books a court and
   * training balls for a full day produces ~20 "6-7PM: Training Balls (Set),
   * Court 3" fragments, which rendered as a 200px-tall wall of text per row
   * and buried the date and status that actually let you recognise your own
   * booking. A short booking still reads literally; anything longer collapses
   * to totals.
   *
   * The detail view below is unchanged — once you've opened a booking, the
   * full breakdown is exactly what you want.
   *
   * @param {object} booking a grouped booking (see groupBookingRecords)
   * @returns {string}
   */
  const summarizeBooking = (booking) => {
    const groups = booking?.dates?.length
      ? booking.dates
      : [{ date: booking?.date, timeSlots: booking?.timeSlots }];

    let hours = 0;
    const courts = new Set();
    groups.forEach(({ timeSlots }) => {
      Object.entries(timeSlots || {}).forEach(([, courtList]) => {
        hours += 1;
        const list = Array.isArray(courtList) ? courtList : Object.values(courtList || {});
        list.forEach(c => courts.add(Number(c)));
      });
    });

    if (hours === 0) return 'No hours booked';

    // Few enough to read at a glance — show the real thing.
    if (hours <= 2) {
      const line = formatTimeSlots(groups[0]?.timeSlots);
      return line.length <= MATCH_SUMMARY_MAX_CHARS ? line : `${line.slice(0, MATCH_SUMMARY_MAX_CHARS - 1).trimEnd()}…`;
    }

    const courtNames = [...courts].sort((a, b) => a - b).map(c => getCourtLabel(config, c));
    // Two names plus a count, rather than every name — a club with six
    // courts would otherwise push the line back over two lines.
    const courtLabel = courtNames.length <= 2
      ? courtNames.join(', ')
      : `${courtNames.slice(0, 2).join(', ')} +${courtNames.length - 2}`;

    const summary = `${hours} hour${hours === 1 ? '' : 's'} · ${courtLabel}`;
    return summary.length <= MATCH_SUMMARY_MAX_CHARS
      ? summary
      : `${summary.slice(0, MATCH_SUMMARY_MAX_CHARS - 1).trimEnd()}…`;
  };

  // Defaults to the (legacy, single-date) bookingData.timeSlots, but also
  // accepts one date group's own timeSlots directly — used to render each
  // row of a multi-date booking's schedule breakdown below.
  const formatTimeSlots = (timeSlots = bookingData?.timeSlots) => {
    if (!timeSlots) return 'N/A';
    if (typeof timeSlots === 'object') {
      return Object.entries(timeSlots).map(([timeSlot, courts]) => {
        const courtList = Array.isArray(courts) ? courts : Object.keys(courts).map(Number);
        // Court names from Config -> Sports & Courts, falling back to
        // "Court N" for any court the admin hasn't named.
        return `${timeSlot}: ${courtList.map(c => getCourtLabel(config, c)).join(', ')}`;
      }).join(' | ');
    }
    return timeSlots.join(', ');
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
      case 'approved':
        return 'confirmed';
      case 'pending':
      case 'awaiting_payment':
      // An Open Play join that never got its receipt attached — the same
      // "we're waiting on you" state as awaiting_payment.
      case 'pending_payment':
        return 'pending';
      case 'cancelled':
        return 'cancelled';
      case 'disapproved':
      // Open Play's word for the same outcome as a disapproved booking.
      case 'declined':
      // The admin took this player off a hosted session after confirming
      // them. Different cause, same thing to say to the guest: they are not
      // on the session any more.
      case 'removed':
        return 'disapproved';
      default:
        return 'pending';
    }
  };

  const getStatusDisplay = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'Confirmed';
      case 'approved':
        return 'Approved';
      case 'pending':
        return 'Pending Approval';
      case 'awaiting_payment':
      case 'pending_payment':
        return 'Awaiting Payment';
      case 'cancelled':
        return 'Cancelled';
      case 'disapproved':
        return 'Disapproved';
      case 'declined':
        return 'Declined';
      case 'removed':
        return 'Removed';
      default:
        return status || 'Unknown';
    }
  };

  return (
    <div className="booking-status-screen">
      <div className="status-header">
        {/* Club logo from Admin Config; ConfigContext falls back to
            public/logo.png, so this is only empty before config loads. */}
        {config?.logoUrl && <img src={config.logoUrl} alt={courtName} className="status-logo" />}
        <h2>{BOOKING_STATUS_TITLE}</h2>
        <p>{BOOKING_STATUS_SUBTITLE}</p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="status-search-form">
        <div className="search-input-group">
          <input
            type="text"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            placeholder="Confirmation ID or the name you booked under"
            className="search-input"
            aria-label="Confirmation ID or booking name"
          />
          <button type="submit" className="search-btn">
            Search
          </button>
        </div>
      </form>

      {/* Loading State — the shared logo-in-ring spinner, same as every
          other loading state in the app (was a bare CSS ring before). */}
      {loading && !searchTimedOut && (
        <div className="status-loading">
          {spinnerRing('Searching for your booking')}
        </div>
      )}

      {/* The lookup ran past the configured timeout. Not gated on `loading`:
          the verdict sticks until Try Again (or a fresh search), so a read
          that lands a minute late can't replace this with a booking card the
          guest has already been told did not load. */}
      {searchTimedOut && (
        <ConnectionErrorScreen onRetry={handleSearchRetry} />
      )}

      {/* Several reservations under the same name — the guest picks. Shows
          only what's needed to tell them apart (when, which court, where it
          stands); no contact details or receipt until one is opened. */}
      {matches.length > 0 && !loading && !searchTimedOut && (
        <div className="status-matches">
          <p className="status-matches-head">
            {matches.length} reservations under &ldquo;{searchId.trim()}&rdquo; — pick yours:
          </p>
          <ul className="status-match-list">
            {matches.map((match) => (
              <li key={match.confirmationId || match.id}>
                <button type="button" className="status-match" onClick={() => handlePickMatch(match)}>
                  <span className="status-match-main">
                    <span className="status-match-date">
                      {formatBookingDate(firstDateOf(match), 'EEE, MMM d, yyyy')}
                      {(match.dates || []).length > 1 ? ` +${match.dates.length - 1} more` : ''}
                    </span>
                    <span className="status-match-slots">{summarizeBooking(match)}</span>
                  </span>
                  <span className={`status-badge ${getStatusClass(match.status)}`}>
                    {getStatusDisplay(match.status)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Error State */}
      {error && !loading && !searchTimedOut && (
        <div className="status-error">
          <div className="error-icon">❌</div>
          <p>{error}</p>
          <button onClick={() => (embedded ? onClose?.() : navigate('/?book=1'))} className="btn-primary">
            Book a Court
          </button>
        </div>
      )}

      {/* Booking Found */}
      {bookingData && !loading && !searchTimedOut && (
        <div className="booking-details">
          <div className="booking-status-card">
            <div className="status-header-section">
              <span className={`status-badge ${getStatusClass(bookingData.status)}`}>
                {getStatusDisplay(bookingData.status)}
              </span>
              <p className="confirmation-id">Ref: {bookingData.confirmationId}</p>
            </div>

            <div className="booking-info-grid">
              {bookingData.isOpenPlayJoin ? (
                <>
                  <div className="info-item">
                    <label>Session</label>
                    <p>{bookingData.sportName ? `${bookingData.sportName} Open Play` : 'Open Play'}</p>
                  </div>
                  <div className="info-item">
                    <label>Date & Time</label>
                    <p className="bss-date">{formatBookingDate(bookingData.date, 'MMMM dd, yyyy')}</p>
                    {/* timeSlot is set only when the guest joined from one
                        specific grid cell; joining from a session card
                        covers the whole session, which has no single hour. */}
                    <p className="bss-time">{bookingData.timeSlot ? formatTimeSlotRange(bookingData.timeSlot) : 'Whole session'}</p>
                  </div>
                  <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                    <label>Players ({bookingData.numPlayers || 0})</label>
                    {(bookingData.players?.length
                      ? bookingData.players
                      // A join saved before rosters were stored has only the booker.
                      : [{ name: bookingData.userName }]
                    ).map((player, index) => (
                      // Names repeat within a group, so the key is the
                      // position — this list is never reordered.
                      <p key={index}>{index + 1}. {player.name}</p>
                    ))}
                  </div>
                </>
              ) : (
                <BookingStatusScheduleBoxes booking={bookingData} />
              )}
              <BookingStatusContactBox booking={bookingData} />
              <BookingStatusPaymentBox booking={bookingData} />
            </div>

            {(bookingData.status === 'pending' || bookingData.status === 'awaiting_payment') && (
              <div className="pending-actions">
                {bookingData.status === 'awaiting_payment' ? (
                  <>
                    <h4>Awaiting Payment</h4>
                    <p>This reservation is still waiting for your payment. If you have already paid, please contact us with your booking reference.</p>
                  </>
                ) : (
                  <>
                    <h4>Pending Approval</h4>
                    <p>Your booking is currently awaiting approval from the administrator.</p>
                  </>
                )}
              </div>
            )}

            {bookingData.status === 'confirmed' && (
              <div className="confirmed-actions">
                <h4>Booking Confirmed!</h4>
                <p>{BOOKING_STATUS_CONFIRMED_MESSAGE.replace('{courtName}', courtName)}</p>
              </div>
            )}

            {bookingData.status === 'cancelled' && (
              <div className="cancelled-actions">
                <h4>Booking Cancelled</h4>
                <p>This booking has been cancelled.</p>
              </div>
            )}

            {bookingData.status === 'disapproved' && (
              <div className="disapproved-actions">
                <h4>Booking Not Approved</h4>
                <p>Unfortunately, your booking was not approved. Please try again with different time slots.</p>
              </div>
            )}
          </div>

          <button onClick={() => (embedded ? onClose?.() : navigate('/'))} className="btn-primary">
            Book Another Court
          </button>
        </div>
      )}
    </div>
  );
}

export default BookingStatusScreen;